import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Spin, Typography } from 'antd';
import { useGetOne } from '../../../Hooks/useGetOne';
import CustomerHeader from './CustomerHeader';
import CustomerInfoCards from './CustomerInfoCards';
import CustomerEditForm from './CustomerEditForm';
import CustomerRegistrationFile from './CustomerRegistrationFile';
import CustomerSummaryStats from './CustomerSummaryStats';
import CustomerTabs from './CustomerTabs';
import { AnimatedContainer } from '../../../Component/ui';
import './CustomerView.scss';

const { Text } = Typography;

const CustomerView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [receiptsPage, setReceiptsPage] = useState(1);

  // Validate ID
  useEffect(() => {
    if (!id || Number.isNaN(Number(id))) {
      console.error('Invalid customer ID:', id);
    }
  }, [id]);

  // Fetch customer basic data
  const {
    data: customerData,
    isLoading: customerLoading,
    error,
    refetch: refetchCustomer,
  } = useGetOne('customers', id || '', {
    baseURL: '/api/admin/customers',
  });

  // Log errors for debugging
  useEffect(() => {
    if (error) {
      console.error('Error loading customer:', error);
    }
  }, [error]);

  // Load tab data only after the customer request succeeds to avoid overloading the server.
  const { data: invoicesData, isLoading: invoicesLoading } = useGetOne(
    'customer-invoices',
    id || '',
    {
      baseURL: id ? `/api/admin/customers/${id}/invoices` : '',
      page: invoicesPage,
      pageSize: 10,
    }
  );

  const { data: receiptsData, isLoading: receiptsLoading } = useGetOne(
    'customer-receipts',
    id || '',
    {
      baseURL: id ? `/api/admin/customers/${id}/receipts` : '',
      page: receiptsPage,
      pageSize: 10,
    }
  );

  // Early return if ID is invalid (after all hooks)
  if (!id || Number.isNaN(Number(id))) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">{t('common.error', 'Invalid customer ID')}</Text>
      </div>
    );
  }

  if (customerLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>{t('common.loading', 'Loading...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <AnimatedContainer className="customer-view" variant="fadeUp">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Text type="danger">{t('common.error', 'Error loading customer data')}</Text>
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => refetchCustomer()}>
              {t('common.refresh', 'Retry')}
            </Button>
          </div>
        </div>
      </AnimatedContainer>
    );
  }

  if (!customerData) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="warning">{t('common.noData', 'No customer data found')}</Text>
      </div>
    );
  }

  // API: { data: { customer, invoices, receipts } }; useGetOne returns inner `data`.
  const payload = customerData?.data ?? customerData;
  const customer = payload?.customer ?? payload;
  const registrationDocuments = payload?.registration_documents || [];

  // Ensure customer exists before rendering (new registrations may have empty name)
  if (!customer?.id) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="warning">{t('common.noData', 'No customer data found')}</Text>
      </div>
    );
  }

  const invoices = invoicesData?.data || [];
  const receipts = receiptsData?.data || [];
  const invoicesTotal = invoicesData?.total || 0;
  const receiptsTotal = receiptsData?.total || 0;

  const handleInvoicesPageChange = (page: number) => {
    setInvoicesPage(page);
  };

  const handleReceiptsPageChange = (page: number) => {
    setReceiptsPage(page);
  };

  return (
    <AnimatedContainer className="customer-view" variant="fadeUp">
      <CustomerHeader
        customerName={customer.name || customer.organization || customer.phone_number || `#${id}`}
        customerId={id}
      />

      <CustomerInfoCards customer={customer} />

      <CustomerEditForm customer={customer} onUpdated={() => refetchCustomer()} />

      <CustomerRegistrationFile
        customer={customer}
        documents={registrationDocuments}
        onUpdated={(customerId) => {
          if (customerId && String(customerId) !== String(id)) {
            history.replace(`/customers/${customerId}`);
            return;
          }
          refetchCustomer();
        }}
      />

      <CustomerSummaryStats customer={customer} />

      <CustomerTabs
        customer={customer}
        invoices={invoices}
        receipts={receipts}
        invoicesLoading={invoicesLoading}
        receiptsLoading={receiptsLoading}
        invoicesPage={invoicesPage}
        receiptsPage={receiptsPage}
        invoicesTotal={invoicesTotal}
        receiptsTotal={receiptsTotal}
        onInvoicesPageChange={handleInvoicesPageChange}
        onReceiptsPageChange={handleReceiptsPageChange}
        onCustomerUpdate={() => {
          // Refresh customer data when loyalty points or products are updated
          window.location.reload();
        }}
      />
    </AnimatedContainer>
  );
};

export default CustomerView;
